import React, { useState, useRef, useEffect, useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { Typography, Box, Table, TableBody, TableCell, TableContainer, TableRow, Paper, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { theme } from "../../../Theme";
import { getUrl } from "src/Utils/Common";
import FacebookCircularProgress from "src/Components/shared/shared/Loading";
import CustomSelectBox from "src/Components/shared/shared/CustomSelectBox";

function SegmentsRetailerValueSalesChart({
  selectedHierarchy: propSelectedHierarchy,
  setSelectedHierarchy: propSetSelectedHierarchy,
  onDataLoaded,
} = {}) {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  
  const projectId = useSelector((state) => state.userProject.projectId);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [hierarchyOptions, setHierarchyOptions] = useState([]);
  const [localSelectedHierarchy, setLocalSelectedHierarchy] = useState("brand");
  const selectedHierarchy =
    propSelectedHierarchy !== undefined ? propSelectedHierarchy : localSelectedHierarchy;
  const setSelectedHierarchy =
    propSetSelectedHierarchy !== undefined ? propSetSelectedHierarchy : setLocalSelectedHierarchy;
  const [loadingHierarchy, setLoadingHierarchy] = useState(true);
  const [categories, setCategories] = useState([]);
  const [valueSales, setValueSales] = useState([]);
  const [avgPricePerKg, setAvgPricePerKg] = useState([]);
  const [valueSalesGrowth, setValueSalesGrowth] = useState([]);
  const [mfcShare, setMfcShare] = useState([]);
  const [wob, setWob] = useState([]);
  const [priceIndexVsAvg, setPriceIndexVsAvg] = useState([]);

  useEffect(() => {
    function fetchHierarchyLevels() {
      if (!projectId) {
        console.warn("Project ID is not set in Redux state.");
        setError("Project ID not available");
        setLoadingHierarchy(false);
        return;
      }
      setLoadingHierarchy(true);
      setError(null);
      fetch(`${getUrl()}/market_overview/hierarchy`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": localStorage.getItem("userId") || "",
          "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
        },
        body: JSON.stringify({ project_id: projectId }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.json();
        })
        .then((result) => {
          console.log("result from hierarchy", result);
          
          const hierarchyData =
            result?.["0"] ||
            result?.Product ||
            result?.product ||
            {};

          const rawHierarchyLevels = Object.keys(hierarchyData);
          if (rawHierarchyLevels.length === 0) {
            throw new Error("No hierarchy data available");
          }

          const hierarchyLevels = rawHierarchyLevels.filter(
            (level) =>
              !["size_bracket", "size", "price_bracket", "sku_db", "sku_id"].includes(level)
          );

          const finalHierarchyOptions = [...hierarchyLevels, "channel", "retailer"];
          setHierarchyOptions(finalHierarchyOptions);
          if (propSetSelectedHierarchy === undefined) {
            setSelectedHierarchy((prev) => {
              if (!prev || !finalHierarchyOptions.includes(prev)) {
                return hierarchyLevels.includes("brand")
                  ? "brand"
                  : hierarchyLevels[0] || "";
              }
              return prev;
            });
          }
          setError(null);
        })
        .catch((error) => {
          console.error("Error fetching hierarchy levels:", error);
          setError(error.message || "Failed to fetch hierarchy levels");
        })
        .finally(() => {
          setLoadingHierarchy(false);
        });
    }

    fetchHierarchyLevels();
  }, [projectId]);

    const fetchData = async () => {
    if (!projectId || !selectedHierarchy) {
      return;
    }

      try {
        setLoading(true);
        setError(null);

        const url = `${getUrl()}/market_overview/segments_market_manufacturer?project_id=${projectId}&comparison_type=P12M&filters={}&hierarchy=${selectedHierarchy}&target=1`;
        const headers = {
          "Content-Type": "application/json",
          "x-access-token": localStorage.getItem("userId") || "",
          "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
        };

        console.groupCollapsed("[SegmentsRetailerValueSalesChart] segments_market_manufacturer request");
        console.log("GET", url);
        console.log("headers", headers);
        console.log("query", {
          project_id: projectId,
          comparison_type: "P12M",
          filters: "{}",
          hierarchy: selectedHierarchy,
          target: 1,
        });
        console.groupEnd();

        const response = await fetch(url, {
          method: "GET",
          headers,
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const text = await response.text();
        const data = JSON.parse(text.replace(/\bNaN\b/g, "null"));
        
          if (data && data.Market) {
            const marketData = data.Market;
            
            const hierarchyData = marketData[selectedHierarchy] || [];
            setCategories(hierarchyData);
            setValueSales((marketData.value_sales || []).map(val => val / 1000000));
          
            setAvgPricePerKg(marketData.price_index || []);
            
            setValueSalesGrowth((marketData.Value_Sales_Growth || []).map(val => val / 100));
            
            setWob(marketData.Value_Share || []);
            
            setPriceIndexVsAvg(marketData.Price_Index_Growth || []);
            
            setMfcShare(marketData.mfc_share ?? []);

            const flatData = marketData[selectedHierarchy].map((name, i) => ({
              name,
              value_sales: marketData.value_sales[i],
              price_index: marketData.price_index[i],
              Value_Sales_Growth: marketData.Value_Sales_Growth[i],
              Value_Share: marketData.Value_Share[i],
              Price_Index_Growth: marketData.Price_Index_Growth[i],
              mfc_share: marketData.mfc_share?.[i]
            }));
            if (onDataLoaded) onDataLoaded(flatData);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
      fetchData();
  }, [projectId, selectedHierarchy]);

  const handleRefresh = () => {
    setCategories([]);
    setValueSales([]);
    setAvgPricePerKg([]);
    setValueSalesGrowth([]);
    setMfcShare([]);
    setWob([]);
    setPriceIndexVsAvg([]);
    setError(null);
    fetchData();
  };

  const getPriceIndexVsAvgColor = (index) => {
    if (Math.abs(index - 100) < 0.01) return "#000000"; 
    return index > 100 ? theme.palette.success.main || "#4CAF50" : "#d32f2f"; 
  };

  const chartRef = useRef(null);

  const maxDeviation = Math.max(
    ...valueSalesGrowth.map(v => Math.abs(v - 1))
  );
  
  const maxBarLength = 20;
  
  const isLoading = loading || loadingHierarchy;

  const series = [
    {
      name: "Value Sales (M)",
      type: "column",
      data: valueSales,
    },
    {
      name: "Avg Price Per Kg (SAR)",
      type: "line",
      data: avgPricePerKg,
    },
  ];

  const options = useMemo(() => ({
       chart: {
         type: "line",
         height: isMobile ? 250 : 280,
         toolbar: {
           show: false,
         },
       },
    colors: [theme.palette.secondary.dark, theme.palette.primary.main],
    plotOptions: {
      bar: {
        columnWidth: "60%",
        borderRadius: 0,
      },
    },
    dataLabels: {
      enabled: true,
      enabledOnSeries: [1],
      formatter: function (val) {
        return `${val.toFixed(2)}`;
      },
      style: {
        fontSize: "11px",
        fontWeight: 600,
        colors: [theme.palette.primary.main],
      },
      offsetY: -10,
      background: {
        enabled: false,
      },
    },
    stroke: {
      width: [0, 0],
    },
    markers: {
      size: [0, 5],
      colors: [theme.palette.primary.main],
      strokeWidth: 2,
      strokeColors: "#fff",
    },
    xaxis: {
      categories: categories,
      labels: {
        rotate: isMobile ? -45 : 0,
        rotateAlways: isMobile ? true : false,
        maxHeight: isMobile ? 60 : undefined,
        offsetY: isMobile ? 5 : -10,
        trim: false,
        hideOverlappingLabels: false,
        style: {
          fontSize: isMobile ? "9px" : "10px",
          whiteSpace: "normal",
          lineHeight: 1.1,
        },
        formatter: function (value) {
          if (!value || typeof value !== "string") return value ?? "";
          const words = value.split(" ");
          
          if (words.length <= 1) return [value];
          
          const lines = [];
          let i = 0;
          
          while (i < words.length) {
            if (i === 0 && words[i].length <= 3 && words.length > 1) {
              lines.push(words.slice(i, i + 2).join(" "));
              i += 2;
            } else {
              lines.push(words[i]);
              i += 1;
            }
          }
          
          return lines;
        },
         },
         offsetX: 0,
         offsetY: isMobile ? 5 : -10,
         style: {
           fontSize: isMobile ? "9px" : "10px",
           whiteSpace: "normal",
           lineHeight: 1.1,
         },
       },
    yaxis: [
      {
        title: {
          text: "Volume (M)",
          style: {
            color: theme.palette.secondary.dark,
            fontSize: "12px",
            fontWeight: 600,
          },
        },
        labels: {
          style: {
            colors: theme.palette.secondary.dark,
          },
          formatter: function (val) {
            return val.toFixed(0);
          },
        },
        min: 0,
        max: 350,
      },
      {
        opposite: true,
        seriesName: "Avg Price Per Kg (SAR)",
        title: {
          text: "Price (SAR)",
          style: {
            color: theme.palette.primary.main,
            fontSize: "12px",
            fontWeight: 600,
          },
        },
        labels: {
          style: {
            colors: theme.palette.primary.main,
          },
          formatter: function (val) {
            return `${val.toFixed(0)}`;
          },
        },
        min: 0,
        max: 16,
      },
    ],
    grid: {
      show: true,
      borderColor: "#e0e0e0",
      strokeDashArray: 0,
      position: "back",
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    legend: {
      show: false,
    },
    tooltip: {
      shared: true,
      intersect: false,
      custom: function ({ series, dataPointIndex }) {
        const category = categories[dataPointIndex];
        const sales = series[0][dataPointIndex];
        const price = series[1][dataPointIndex];

        return `
          <div style="padding: 10px; background: #fff; border: 1px solid #ccc; border-radius: 4px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
            <div style="font-weight: 600; margin-bottom: 5px;">${category}</div>
            <div style="color: ${
              theme.palette.secondary.dark
            };">Value Sales: ${sales.toFixed(1)} M</div>
            <div style="color: ${
              theme.palette.primary.main
            };">Avg Price: ${price.toFixed(2)} SAR</div>
          </div>
        `;
      },
    },
  }), [categories, selectedHierarchy, isMobile]);

  return (
    <Box
      sx={{
        width: { xs: "100%", sm: "90%", md: "75%" },
        mx: "auto",
      }}
    >
      <Typography
        sx={{
          fontWeight: "bold",
          fontSize: { xs: "0.95rem", sm: "1.05rem", md: "1.15rem" },
          px: { xs: 0.5, sm: 0.75, md: 1 },
          color: theme.palette.primary.main,
          textAlign: "center",
          lineHeight: 1,
          mb: 2,
        }}
      >
        Segments/Retailer Value Sales & Avg Price Per Kg
      </Typography>

      <Box
        display="flex"
        justifyContent="center"
        mb={{ xs: 1.5, sm: 2, md: 4}}
        px={{ xs: 1, sm: 0 }}
      >
        <CustomSelectBox
          sx={{
            fontWeight: 500,
            fontSize: {
              xs: "14px",
              sm: "16px",
              md: "18px",
            },
            height: {
              xs: 32,
              sm: 36,
              md: 40,
            },
            minHeight: {
              xs: 32,
              sm: 36,
              md: 40,
            },
            maxHeight: {
              xs: 32,
              sm: 36,
              md: 40,
            },
            p: 0,
            mb: 2,
            display: "flex",
            alignItems: "center",
          }}
          value={selectedHierarchy}
          onChange={(event) => {            
            setSelectedHierarchy(event.target.value);
          }}
          options={hierarchyOptions}
        />
      </Box>

      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <FacebookCircularProgress loading={true} hasData={true} />
        </Box>
      ) : error || !categories || categories.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <FacebookCircularProgress 
            loading={false}
            hasData={false}
            onRefresh={handleRefresh}
            message={
              error
                ? `Error loading data: ${error} - Click to retry`
                : "No data available - Click to reset chart"
            }
          />
        </Box>
      ) : (
        <>
        <Box sx={{ position: "relative", width: "100%" }} ref={chartRef}>
        {/* Bars above the charts */}
        <Box
          sx={{
            position: "absolute",
            top: -28,
            left: "8%",
            right: "6%",
            height: 32,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pointerEvents: "auto",
            zIndex: 10,
          }}
        >
          {/* Baseline - horizontal grey line (invisible) */}
          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              top: "50%",
              height: "1px",
              backgroundColor: "transparent",
              zIndex: 1,
              pointerEvents: "none",
            }}
          />

          {categories.map((cat, index) => {
            const indexValue = valueSalesGrowth[index];
            const deviation = indexValue - 1;
            const barHeight =
              maxDeviation > 0
                ? Math.abs(deviation) * (maxBarLength / maxDeviation)
                : 0;
            const barColor =
              indexValue < 1 ? "#d32f2f" : theme.palette.secondary.dark;
            const textColor =
              indexValue < 1 ? "#d32f2f" : theme.palette.secondary.dark;

            return (
              <Tooltip
                key={index}
                title={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {cat}
                    </Typography>
                    <Typography variant="body2">
                      Value Sales Growth: {indexValue.toFixed(2)}
                    </Typography>
                  </Box>
                }
                arrow
                placement="top"
              >
                <Box
                  sx={{
                    position: "relative",
                    width: `${100 / categories.length}%`,
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 2,
                    cursor: "pointer",
                  }}
                >
                {indexValue > 1 && (
                  <>
                    <Typography
                      sx={{
                        position: "absolute",
                        top: `calc(50% - ${barHeight}px - 12px)`,
                        fontSize: "10px",
                        fontWeight: 600,
                        color: textColor,
                        lineHeight: 1,
                        width: "100%",
                        textAlign: "center",
                      }}
                    >
                      {indexValue.toFixed(2)}
                    </Typography>
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: "50%",
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "55%",
                        height: `${barHeight}px`,
                        backgroundColor: barColor,
                      }}
                    />
                  </>
                )}

                {indexValue === 1 && (
                  <Typography
                    sx={{
                      fontSize: "10px",
                      fontWeight: 600,
                      color: textColor,
                      lineHeight: 1,
                    }}
                  >
                    1
                  </Typography>
                )}
                {indexValue < 1 && (
                  <>
                    <Box
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "55%",
                        height: `${barHeight}px`,
                        backgroundColor: barColor,
                      }}
                    />
                    <Typography
                      sx={{
                        position: "absolute",
                        bottom: `calc(50% - ${barHeight}px - 12px)`,
                        fontSize: "10px",
                        fontWeight: 600,
                        color: textColor,
                        lineHeight: 1,
                        width: "100%",
                        textAlign: "center",
                      }}
                    >
                      {indexValue.toFixed(2)}
                    </Typography>
                  </>
                )}
                </Box>
              </Tooltip>
            );
          })}
        </Box>

        <ReactApexChart
          options={options}
          series={series}
          type="line"
          height={isMobile ? 250 : 280}
          width="100%"
        />
      </Box>

      {/* Table */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          overflowX: "visible",
          p: 1.5,
          width: "100%",
          border: "none",
          boxShadow: "none",
          backgroundColor: "transparent",
        }}
      >
        <Table
          size="small"
          sx={{
            tableLayout: "fixed",
            width: "100%",
            "& .MuiTableCell-root": {
              borderBottom: "2px solid rgba(255, 255, 255, 0.7)",
            },
          }}
        >
          <colgroup>
            <col style={{ width: isMobile ? "12%" : "7%" }} />
            {categories.map(() => (
              <col
                key={Math.random()}
                style={{ width: `${(isMobile ? 81 : 86) / categories.length}%` }}
              />
            ))}
            <col style={{ width: "5%" }} />
          </colgroup>
          <TableBody>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 600,
                  backgroundColor: theme.palette.primary.main,
                  color: "#fff",
                  fontSize: isMobile ? "9px" : "12px",
                  minWidth: 150,
                  width: isMobile ? "12%" : "7%",
                  py: 0.5,
                  px: 1,
                }}
              >
                Mfc. Share
              </TableCell>
              {mfcShare.map((share, index) => (
                <TableCell
                  key={index}
                  align="center"
                  sx={{
                    fontSize: "11px",
                    backgroundColor: "#f5f5f5",
                    py: 0.5,
                  }}
                >
                  {`${share}%`}
                </TableCell>
              ))}
              <TableCell
                sx={{ width: "5%", backgroundColor: "transparent", py: 0.5 }}
              />
            </TableRow>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 600,
                  backgroundColor: theme.palette.primary.main,
                  color: "#fff",
                  fontSize: isMobile ? "9px" : "12px",
                  py: 0.5,
                  px: 1,
                }}
              >
                WOB (Mkt)
              </TableCell>
              {wob.map((w, index) => (
                <TableCell
                  key={index}
                  align="center"
                  sx={{
                    fontSize: "11px",
                    backgroundColor: "#f5f5f5",
                    py: 0.5,
                  }}
                >
                  {w}%
                </TableCell>
              ))}
              <TableCell
                sx={{ width: "5%", backgroundColor: "transparent", py: 0.5 }}
              />
            </TableRow>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 600,
                  backgroundColor: theme.palette.primary.main,
                  color: "#fff",
                  fontSize: isMobile ? "9px" : "12px",
                  py: 0.5,
                  px: 1,
                }}
              >
                Price Index vs. Avg Mkt
              </TableCell>
              {priceIndexVsAvg.map((index, idx) => (
                <TableCell
                  key={idx}
                  align="center"
                  sx={{
                    fontSize: "11px",
                    backgroundColor: "#f5f5f5",
                    color: getPriceIndexVsAvgColor(index),
                    py: 0.5,
                  }}
                >
                  {index}
                </TableCell>
              ))}
              <TableCell
                sx={{ width: "5%", backgroundColor: "transparent", py: 0.5 }}
              />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
        </>
      )}
    </Box>
  );
}

export default SegmentsRetailerValueSalesChart;

